import type {
  ChannelThreadId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { Message } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { ChannelThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import PhoneCallIcon from "@redotech/redo-icons/phone-call.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useState, type MouseEvent } from "react";
import { dispatchVoiceCallCustomer } from "../../voice/voice-call-customer-event";
import { VoiceMessageCard } from "../message-types/voice-message-card";
import { ThreadHeader } from "../thread-header";

/**
 * Voice thread view — renders each voice message as a card with the
 * call direction, status, duration, and an inline recording player.
 * Read-only; voice threads have no reply input (you call back, you
 * don't text-reply).
 */
export const VoiceThreadView = memo(function VoiceThreadView({
  messages,
  supportTicketId,
  thread,
}: {
  messages: Message[];
  messageThreadIds?: readonly ChannelThreadId[];
  supportTicketId: SupportTicketId;
  thread: ChannelThread;
}) {
  const voiceMessages = useMemo(
    () =>
      (messages ?? []).filter(
        (m): m is Extract<Message, { channel: "voice" }> =>
          m.channel === "voice",
      ),
    [messages],
  );

  const customerNumber =
    thread.integrationType === "telnyx_voice"
      ? (thread.channelData?.customerNumber ?? null)
      : null;
  const latestMessageTime = voiceMessages.at(-1)?.sentAt;

  const [isCollapsed, setIsCollapsed] = useState(false);
  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const handleCallBack = useCallback(
    (e: MouseEvent) => {
      // Prevent the header's collapse toggle from firing alongside.
      e.stopPropagation();
      if (customerNumber) {
        dispatchVoiceCallCustomer(customerNumber);
      }
    },
    [customerNumber],
  );

  return (
    <Flex dir="column" gap="none">
      <ThreadHeader
        channel="voice"
        isCollapsed={isCollapsed}
        latestMessageTime={latestMessageTime}
        onClick={toggleCollapsed}
        subject={null}
        trailing={
          customerNumber ? (
            <RedoButton
              hierarchy="secondary"
              IconLeading={PhoneCallIcon}
              onClick={handleCallBack}
              size="xs"
              text="Call back"
            />
          ) : undefined
        }
      />
      {!isCollapsed && (
        <Flex
          bgColor="primary"
          borderBottomWidth="1px"
          borderColor="primary"
          borderLeftWidth="1px"
          borderRightWidth="1px"
          borderStyle="solid"
          bottomLeftRadius="sm"
          bottomRightRadius="sm"
          dir="column"
          gap="md"
          p="xl"
        >
          {voiceMessages.length === 0 ? (
            <Flex align="center" justify="center" p="xl">
              <Text fontSize="md" textColor="secondary">
                No calls in this thread
              </Text>
            </Flex>
          ) : (
            voiceMessages.map((m) => (
              <VoiceMessageCard
                customerNumber={customerNumber}
                key={m.id}
                message={m}
              />
            ))
          )}
        </Flex>
      )}
    </Flex>
  );
});
